import { useEffect, useRef, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { miscellaneousType, paymentModes } from "../../utils";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { formatDate } from "../../functions";
import { getDecodedToken } from "../../auth";
import { useDispatch, useSelector } from "react-redux";
import { fetchMiscelleneousFeesDetails } from "../../redux/miscelleneousFeesSlice";
import MiscellaneousFeeReceipt from "../../components/MiscellaneousFeeReceipt";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function MiscellaneousFees() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const dateInputRef = useRef(null);

  useEffect(() => {
    const decodedData = getDecodedToken();
    if (decodedData) {
      setUser(decodedData);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.userId) {
      setFormData((prev) => ({
        ...prev,
        collectedBy: user.userId,
      }));
      setLoading(false);
    }
  }, [user]);

  const fetchStudentDetails = async (admissionNo) => {
    try {
      const response = await axios.post(
        `${server}/getParticularStudentMongodb`,
        { admission_no: admissionNo },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const studentData = response.data?.["body-json"]?.body?.student || null;

      if (!studentData) {
        console.warn(`No student found for admission number: ${admissionNo}`);
      }

      return studentData;
    } catch (error) {
      console.error(
        `Error fetching student details for ${admissionNo}:`,
        error,
      );
      return null;
    }
  };

  const handleDownloadReceipt = async (receiptData) => {
    try {
      const student = await fetchStudentDetails(receiptData?.formNo);
      const mergedData = {
        ...receiptData,
        student,
      };
      MiscellaneousFeeReceipt([mergedData]);
    } catch (error) {
      console.error("Error generating receipt:", error);
    }
  };

  const [formData, setFormData] = useState({
    type: "",
    formNo: "",
    session: "2025-26",
    studentName: "",
    amount: "",
    mode: "",
    paymentId: "",
    bank: "",
    collectedBy: user?.userId,
  });

  const paymentModeLabels = {
    UPI: "UPI ID",
    NEFT: "UTR No",
    Cheque: "Cheque No",
    "Card Swap": "Card Last 4 Digit No",
    "Net Banking": "Reference ID",
  };

  const bankApplicableModes = [
    "NEFT",
    "Card Swap",
    "Net Banking",
    "Cheque",
    "UPI",
  ];

  const dispatch = useDispatch();
  const { data: miscelleneousFeesDetails, loading: fetchLoading } = useSelector(
    (state) => state.miscelleneousFees,
  );

  useEffect(() => {
    dispatch(fetchMiscelleneousFeesDetails());
  }, [dispatch]);

  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "formNo" && value.trim().length >= 3) {
      const student = await fetchStudentDetails(value);

      if (student) {
        setFormData((prev) => ({
          ...prev,
          studentName: student.name || "",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          studentName: "",
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${server}/miscelleneous_fee_history_mongodb`,
        formData,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Payment Collected", { position: "top-right" });
      fetchMiscelleneousFeesDetails();

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }

      setFormData({
        type: "",
        formNo: "",
        session: "2025-26",
        studentName: "",
        amount: "",
        mode: "",
        paymentId: "",
        bank: "",
        collectedBy: user?.userId,
      });

      dispatch(fetchMiscelleneousFeesDetails());

      setError({});
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const excludedFields = ["session", "collectedBy"];

  const filteredFormKeys = Object.keys(formData).filter(
    (key) => !excludedFields.includes(key),
  );

  const totalFields = filteredFormKeys.length;

  const filledFields = filteredFormKeys.filter((key) => {
    const val = formData[key];
    return (
      (typeof val === "string" && val.trim() !== "") ||
      (typeof val === "number" && val > 0)
    );
  }).length;

  // Filter
  const filteredRecords = miscelleneousFeesDetails.filter((feesMaster) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(feesMaster || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDownloadMiscReport = (type) => {
    if (!miscelleneousFeesDetails || miscelleneousFeesDetails.length === 0) {
      toast.error("No miscellaneous fee data available.");
      return;
    }

    const formatData = (data) =>
      data.map((item) => ({
        "Receipt No": item.receipt_no || "",
        "Form No": item.formNo || "",
        "Student Name": item.studentName || "",
        Type: item.type || "",
        Amount: item.amount || "",
        Mode: item.mode || "",
        "Payment ID": item.paymentId || "",
        Bank: item.bank || "",
        "Collected By": item.collectedBy || "",
        Date: new Date(item.createdAt).toLocaleDateString("en-GB"),
      }));

    const workbook = XLSX.utils.book_new();

    // 1️⃣ All Fees Report
    if (type === "all") {
      const wsAll = XLSX.utils.json_to_sheet(
        formatData(miscelleneousFeesDetails),
      );
      XLSX.utils.book_append_sheet(workbook, wsAll, "All Fees");
    }

    // 2️⃣ Month Wise Report
    if (type === "month") {
      const monthGroups = {};
      miscelleneousFeesDetails.forEach((rec) => {
        const date = rec.createdAt ? new Date(rec.createdAt) : null;
        const monthName = date
          ? date.toLocaleString("default", { month: "long" })
          : "Unknown";
        if (!monthGroups[monthName]) monthGroups[monthName] = [];
        monthGroups[monthName].push(rec);
      });

      Object.entries(monthGroups).forEach(([month, data]) => {
        const ws = XLSX.utils.json_to_sheet(formatData(data));
        XLSX.utils.book_append_sheet(workbook, ws, month);
      });
    }

    // 3️⃣ Type Wise Report
    if (type === "type") {
      const typeGroups = {};
      miscelleneousFeesDetails.forEach((rec) => {
        const t = rec.type || "Unspecified";
        if (!typeGroups[t]) typeGroups[t] = [];
        typeGroups[t].push(rec);
      });

      Object.entries(typeGroups).forEach(([t, data]) => {
        const ws = XLSX.utils.json_to_sheet(formatData(data));
        // Clean up invalid characters in sheet name
        const safeType = t.replace(/[\\/*?:[\]]/g, "_");
        XLSX.utils.book_append_sheet(workbook, ws, safeType);
      });
    }

    // Create Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Determine file name
    const fileNameMap = {
      all: "Miscellaneous_All_Report.xlsx",
      month: "Miscellaneous_Month_Wise_Report.xlsx",
      type: "Miscellaneous_Type_Wise_Report.xlsx",
    };
    saveAs(blob, fileNameMap[type] || "Miscellaneous_Report.xlsx");
    toast.success("Report Generated Successfully");
  };

  const handleDownloadDateWiseMiscReport = () => {
    const selectedDate = dateInputRef.current?.value;

    if (!selectedDate) {
      toast.error("Please select a date first.");
      return;
    }

    // Convert to DD/MM/YYYY for filtering
    const formattedDate = new Date(selectedDate).toLocaleDateString("en-GB");

    const filteredData = miscelleneousFeesDetails.filter((item) => {
      const recordDate = new Date(item.createdAt).toLocaleDateString("en-GB");
      return recordDate === formattedDate;
    });

    if (filteredData.length === 0) {
      toast.info(`No records found for ${formattedDate}.`);
      return;
    }

    // Format the data for Excel
    const formatData = (data) =>
      data.map((item) => ({
        "Receipt No": item.receipt_no || "",
        "Form No": item.formNo || "",
        "Student Name": item.studentName || "",
        Type: item.type || "",
        Amount: item.amount || "",
        Mode: item.mode || "",
        "Payment ID": item.paymentId || "",
        Bank: item.bank || "",
        "Collected By": item.collectedBy || "",
        Date: new Date(item.createdAt).toLocaleDateString("en-GB"),
      }));

    // Create Excel workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(formatData(filteredData));

    // ✅ Fix sheet name — Excel cannot have / \ ? * [ ] :
    const safeSheetName = formattedDate.replaceAll("/", "-");
    XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);

    // Create Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Save the file
    const fileName = `Miscellaneous_Report_${safeSheetName}.xlsx`;
    saveAs(blob, fileName);

    toast.success(`Report generated for ${formattedDate}`);
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div
              class="modal fade gd-example-modal-lg"
              tabindex="-1"
              role="dialog"
              aria-labelledby="myLargeModalLabel"
              aria-hidden="true"
            >
              <div class="modal-dialog modal-lg">
                <div class="modal-content">
                  <form onSubmit={handleSubmit}>
                    <div class="modal-header d-flex justify-content-between">
                      <h5 class="modal-title fs-4" id="exampleModalToggleLabel">
                        Collect Miscellaneous Fee{" "}
                        <span className="ms-3 text-secondary">
                          ({String(filledFields).padStart(2, "0")}/
                          {String(totalFields).padStart(2, "0")})
                        </span>
                      </h5>
                      <div className="d-flex align-items-center">
                        <span class="badge bg-success-soft me-3 px-3">
                          Editable
                        </span>
                        <div class="form-check form-switch me-4">
                          <input
                            type="checkbox"
                            class="form-check-input m-0"
                            checked
                          />
                        </div>
                        <button
                          type="button"
                          class="btn-close"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        ></button>
                      </div>
                    </div>

                    <div class="modal-body">
                      <div className="row g-2 mb-2">
                        {/* Type */}
                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Type <span className="text-danger">*</span>
                          </label>
                          <Select
                            name="type"
                            value={
                              miscellaneousType
                                .map((cls) => ({ label: cls, value: cls }))
                                .find((opt) => opt.value === formData.type) ||
                              null
                            }
                            onChange={(selected) =>
                              handleChange({
                                target: {
                                  name: "type",
                                  value: selected?.value || "",
                                },
                              })
                            }
                            options={miscellaneousType.map((cls) => ({
                              label: cls,
                              value: cls,
                            }))}
                            placeholder="Select Type"
                          />
                        </div>

                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Admission No.
                          </label>
                          <input
                            type="text"
                            maxLength={5}
                            className="form-control text-dark"
                            name="formNo"
                            value={formData.formNo}
                            onChange={handleChange}
                            placeholder="Enter Admission No."
                          />
                        </div>

                        {/* Student Name */}
                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Student Name
                          </label>
                          <input
                            type="text"
                            className="form-control text-dark"
                            name="studentName"
                            value={formData.studentName}
                            placeholder="Enter Student Name"
                            readOnly
                          />
                        </div>

                        {/* Amount */}
                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Amount <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control text-dark"
                            name="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            placeholder="Enter Amount"
                          />
                        </div>

                        {/* Payment Mode */}
                        <div className="col-lg-6 col-12">
                          <label className="form-label mb-1 ms-3">
                            Payment Mode <span className="text-danger">*</span>
                          </label>
                          <Select
                            name="mode"
                            value={
                              paymentModes
                                .map((mode) => ({ label: mode, value: mode }))
                                .find((opt) => opt.value === formData.mode) ||
                              null
                            }
                            onChange={(selected) => {
                              const selectedMode = selected?.value || "";
                              setFormData((prev) => ({
                                ...prev,
                                mode: selectedMode,
                                paymentId: "",
                                bank: bankApplicableModes.includes(selectedMode)
                                  ? prev.bank
                                  : "",
                              }));
                            }}
                            options={paymentModes.map((mode) => ({
                              label: mode,
                              value: mode,
                            }))}
                            placeholder="Select Payment Mode"
                          />
                        </div>

                        {/* Payment ID / UTR / Cheque No */}
                        {formData.mode && formData.mode !== "Cash" && (
                          <div className="col-6">
                            <label className="form-label mb-1 ms-3">
                              {paymentModeLabels[formData.mode] ||
                                "Reference No"}{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control text-dark"
                              name="paymentId"
                              value={formData.paymentId}
                              onChange={handleChange}
                              placeholder={`Enter ${
                                paymentModeLabels[formData.mode] ||
                                "Reference No"
                              }`}
                            />
                          </div>
                        )}

                        {/* Bank Field for Specific Modes */}
                        {bankApplicableModes.includes(formData.mode) && (
                          <div className="col-6">
                            <label className="form-label mb-1 ms-3">
                              Bank <span className="text-danger">*</span>
                            </label>
                            <Select
                              name="bank"
                              className="text-dark"
                              value={
                                [
                                  { value: "HDFC", label: "HDFC Bank" },
                                  {
                                    value: "PNB",
                                    label: "Punjab National Bank (PNB)",
                                  },
                                ].find((b) => b.value === formData.bank) || null
                              }
                              onChange={(selectedOption) =>
                                handleChange({
                                  target: {
                                    name: "bank",
                                    value: selectedOption?.value || "",
                                  },
                                })
                              }
                              options={[
                                { value: "HDFC", label: "HDFC Bank" },
                                {
                                  value: "PNB",
                                  label: "Punjab National Bank (PNB)",
                                },
                              ]}
                              placeholder="Select Bank"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div class="modal-footer d-flex justify-content-between py-2">
                      <div>
                        <button
                          type="button"
                          class="btn btn-secondary px-3 me-1 rounded-pill"
                          data-bs-dismiss="modal"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                        <button
                          type="submit"
                          class="btn btn-primary px-5 rounded-pill fw-normal"
                          disabled={loading}
                        >
                          {loading ? (
                            <div
                              class="spinner-border spinner-border-sm"
                              role="status"
                            ></div>
                          ) : (
                            "Submit"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="">
              <div class="container-fluid my-3 px-3">
                <div className="row g-2">
                  <div className="col-lg-8">
                    <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                      <table class="table mb-0 text-nowrap table-centered">
                        <thead class="table-light">
                          <tr>
                            <th className="w-10">No.</th>
                            <th className="lh-1 w-60">
                              Details
                              <i className="bi bi-info-circle ms-2 text-dark"></i>
                              <br />
                              <span className="fs-8 fw-normal text-secondary">
                                Displaying Information as per Record
                              </span>
                            </th>
                            <th className="lh-1 w-30">
                              Action
                              <i className="bi bi-person-gear ms-2 text-dark"></i>
                              <br />
                              <span className="fs-8 fw-normal text-secondary">
                                Role-Specific Action Panel
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentRecords.map((feesMaster, index) => (
                            <tr key={index}>
                              <td>{indexOfFirstRecord + index + 1}.</td>
                              <td className="lh-sm py-4 text-wrap">
                                <span class="text-dark">Payment Type</span>{" "}
                                &mdash;{" "}
                                <span className="text-capitalize">
                                  {feesMaster.type}
                                </span>
                                <br />
                                <span class="text-dark">Receipt No</span>{" "}
                                &mdash; {feesMaster?.receipt_no} <br />
                                <span class="text-dark">
                                  Form No
                                </span> &mdash;{" "}
                                <span className="text-capitalize">
                                  {feesMaster.formNo}
                                </span>
                                <hr className="w-25" />
                                <span class="text-dark">Student Name</span>{" "}
                                &mdash; {feesMaster?.studentName}
                                <br />
                                <span class="text-dark">
                                  Amount
                                </span> &mdash; {feesMaster?.amount}
                                <br />
                                <span className="text-dark">
                                  Mode
                                </span> &mdash; {feesMaster?.mode}
                                <br />
                                {feesMaster?.mode !== "Cash" && (
                                  <>
                                    <span className="text-dark">
                                      Payment Id
                                    </span>{" "}
                                    &mdash; {feesMaster?.paymentId}
                                    <br />
                                    <span className="text-dark">Bank</span>{" "}
                                    &mdash; {feesMaster?.bank}
                                    <br />
                                  </>
                                )}
                                <span class="text-dark">Collected By</span>{" "}
                                &mdash; {feesMaster?.collectedBy}
                                <br />
                                <span class="text-dark">Date</span> &mdash;{" "}
                                {formatDate(feesMaster?.createdAt)}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1 width-fit-content">
                                  <span
                                    onClick={() =>
                                      handleDownloadReceipt(feesMaster)
                                    }
                                    class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Download
                                  </span>
                                  <span class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                    Delete
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div class="">
                      <div className="mb-3">
                        <div className="d-flex bg-white justify-content-between rounded-4">
                          <form
                            className="d-flex align-items-center"
                            onSubmit={(e) => e.preventDefault()}
                          >
                            <span className="position-absolute ps-3 search-icon">
                              <i className="bi bi-filter-circle-fill ms-1" />
                            </span>
                            <input
                              type="search"
                              className="form-control ps-7 rounded-3 border-0 rounded-4"
                              placeholder="Filter Data"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </form>
                          <Link
                            data-bs-toggle="modal"
                            data-bs-target=".gd-example-modal-lg"
                            className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                          >
                            Collect Fees
                          </Link>
                        </div>
                        <div className="mt-2 ms-4">
                          <nav aria-label="breadcrumb">
                            <ol class="breadcrumb pe-3">
                              <li class="breadcrumb-item">
                                <a href="#">Fees Collection</a>
                              </li>
                              <li
                                class="breadcrumb-item active"
                                aria-current="page"
                              >
                                Miscellaneous Fees
                              </li>
                            </ol>
                          </nav>
                        </div>
                      </div>

                      <div className="p-4 bg-white rounded-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            {fetchLoading ? (
                              <div
                                className="spinner-border text-dark data-loader-spinner"
                                role="status"
                              ></div>
                            ) : (
                              <img src="/assets/images/folder.svg" />
                            )}

                            <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                              Miscellaneous Fees
                              <br />
                              <span className="text-secondary fs-6 fw-medium lh-sm">
                                0.6 MB Size
                              </span>
                            </p>
                          </div>
                          <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                            Export
                          </span>
                        </div>

                        <div class="px-4 py-2 mt-3 bg-light rounded-4">
                          <li className="list-group-item px-0">
                            <a
                              className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                              data-bs-toggle="collapse"
                              href="#specifications"
                              role="button"
                              aria-expanded="false"
                              aria-controls="specifications"
                            >
                              <p className="m-0 fw-medium lh-sm text-dark">
                                Download Report
                              </p>
                              <span className="chevron-arrow ms-4">
                                <i className="bi bi-chevron-down fs-4" />
                              </span>
                            </a>
                            <div
                              className="collapse"
                              id="specifications"
                              data-bs-parent="#ecommerceAccordion"
                            >
                              <div className="py-3">
                                <div class="rounded-4 d-flex flex-wrap">
                                  <span
                                    className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                    onClick={() =>
                                      handleDownloadMiscReport("all")
                                    }
                                  >
                                    All Fees Report
                                  </span>
                                  <span
                                    className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                    onClick={() =>
                                      handleDownloadMiscReport("month")
                                    }
                                  >
                                    Month Wise Report
                                  </span>
                                  <span
                                    className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                    onClick={() =>
                                      handleDownloadMiscReport("type")
                                    }
                                  >
                                    Type Wise Report
                                  </span>
                                </div>
                              </div>
                            </div>
                          </li>
                        </div>

                        <div className="bg-secondary-soft rounded-4 py-3 px-4 mt-1">
                          <div>
                            <input
                              type="date"
                              className="form-control ps-4 pe-5 border-0 rounded-pill"
                              placeholder="..."
                              ref={dateInputRef}
                            />
                          </div>
                          <span
                            className="btn btn-primary btn-sm px-4 rounded-pill mt-2 me-1"
                            onClick={handleDownloadDateWiseMiscReport}
                          >
                            Download Report
                          </span>
                        </div>
                      </div>

                      <div className="my-4">
                        <nav>
                          <ul className="pagination justify-content-center mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button className="page-link" onClick={prevPage}>
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>

                            {(() => {
                              const pageNumbers = [];
                              pageNumbers.push(1);

                              if (currentPage > 3) {
                                pageNumbers.push("left-ellipsis");
                              }

                              if (
                                currentPage !== 1 &&
                                currentPage !== totalPages
                              ) {
                                pageNumbers.push(currentPage);
                              }

                              if (currentPage < totalPages - 2) {
                                pageNumbers.push("right-ellipsis");
                              }

                              if (totalPages > 1) {
                                pageNumbers.push(totalPages);
                              }

                              return pageNumbers.map((page, index) => {
                                if (
                                  page === "left-ellipsis" ||
                                  page === "right-ellipsis"
                                ) {
                                  return (
                                    <li
                                      key={index}
                                      className="page-item disabled"
                                    >
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }

                                return (
                                  <li
                                    key={index}
                                    className={`page-item ${
                                      currentPage === page ? "active" : ""
                                    }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              });
                            })()}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button className="page-link" onClick={nextPage}>
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default MiscellaneousFees;
